import type { HTMLAttributes, ReactNode, Ref } from 'react'
import { cx, isFilled } from '@/shared/lib'
import s from './Alert.module.css'

/**
 * Смысловой тон сообщения. Имена совпадают с `StatusBadge`, `Chip` и `ProgressBar`:
 * `danger` — действие не пройдёт, `warning` — пройдёт, но с последствием,
 * `success` — проверка прошла, `accent` — условие, о котором надо знать заранее.
 */
export type AlertTone = 'accent' | 'success' | 'warning' | 'danger'

/** Плотность: `sm` — строка в списке проверок перед отправкой, `md` — самостоятельный алерт. */
export type AlertSize = 'sm' | 'md'

/** Маркер слева. `none` — когда алерт стоит внутри уже размеченного блока и точка становится шумом. */
export type AlertIndicator = 'dot' | 'none'

/**
 * Живость сообщения. Ось намеренно отдельная от тона: тон — про смысл, `announce` — про
 * происхождение узла. Красная подсказка, отрисованная вместе со страницей, объявления не требует,
 * а зелёный ответ 1С, пришедший через секунду, — требует. Разбор в контракте.
 */
export type AlertAnnounce = 'off' | 'polite' | 'assertive'

export type AlertProps = {
  /** Текст сообщения, готовый с бэкенда. Пустой вместе с пустым `heading` — блок не рисуется. */
  children: ReactNode
  /** Заголовок над текстом. Не `title`: тот занят нативным тултипом и уходит в `...rest`. */
  heading?: ReactNode
  tone?: AlertTone
  size?: AlertSize
  indicator?: AlertIndicator
  /** Слот вместо точки: иконка в кружке с заливкой тона. Перекрывает `indicator`. */
  icon?: ReactNode
  /** Слот действия под текстом: `Link` («Как считается цена») или `Button` ступени `sm`. */
  action?: ReactNode
  announce?: AlertAnnounce
  ref?: Ref<HTMLDivElement>
} & Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'role'>

// Карта классов при noUncheckedIndexedAccess приходит как string | undefined
const TONE_CLASS: Record<AlertTone, string | undefined> = {
  accent: s.toneAccent,
  success: s.toneSuccess,
  warning: s.toneWarning,
  danger: s.toneDanger,
}

const SIZE_CLASS: Record<AlertSize, string | undefined> = {
  sm: s.sm,
  md: s.md,
}

/**
 * `alert` перебивает текущую речь, `status` встаёт в очередь. Обе роли неявно дают
 * `aria-atomic="true"` — заголовок и текст читаются одной фразой, а не по кускам.
 */
const ROLE: Record<AlertAnnounce, 'alert' | 'status' | undefined> = {
  off: undefined,
  polite: 'status',
  assertive: 'alert',
}

/**
 * Сообщение об условии, которое влияет на действие пользователя прямо здесь: лимит выбран,
 * скидка уйдёт на утверждение, реквизиты сверены с 1С.
 *
 * Блок в потоке страницы: тост исчезает, баннер живёт на вершине страницы, алерт стоит рядом
 * с тем, к чему относится, и держится, пока держится условие. Смысл несёт текст — маркер и цвет
 * только ускоряют сканирование.
 *
 * Контракт: docs/ui-kit/contracts/alert.md
 */
export function Alert({
  children,
  heading,
  tone = 'accent',
  size = 'md',
  indicator = 'dot',
  icon,
  action,
  announce = 'off',
  className,
  ref,
  ...rest
}: AlertProps) {
  // Пустая цветная плашка — визуальный мусор: серверное поле приходит пустым чаще, чем заполненным
  if (!isFilled(heading) && !isFilled(children)) return null

  const hasHeading = isFilled(heading)
  const showDot = !icon && indicator === 'dot'

  return (
    <div
      {...rest}
      ref={ref}
      className={cx(s.root, SIZE_CLASS[size], TONE_CLASS[tone], className)}
      role={ROLE[announce]}
      data-tone={tone}
      data-size={size}
      data-announce={announce}
    >
      {/* Маркер декоративен: он дублирует тон, а тон дублирует текст */}
      {icon ? (
        <span className={s.marker} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      {showDot ? <span className={s.dot} aria-hidden="true" /> : null}

      <div className={s.content}>
        {hasHeading ? <strong className={s.heading}>{heading}</strong> : null}
        {isFilled(children) ? (
          <span className={cx(s.text, !hasHeading && s.textLead)}>{children}</span>
        ) : null}
        {action ? <div className={s.action}>{action}</div> : null}
      </div>
    </div>
  )
}
